// PortfolioUtilButtons.js
import React, {useEffect, useRef, useState} from "react";
import {useRowSelectionContext} from "@portfolio/context/RowSelectionContextProvider";
import useSearchEscapeHandler from "@portfolio/utils/useSearchEscapeHandler";
import ImportContractNote from "@portfolio/importcnote/ImportContractNote";
import ContractNoteManagerPopup from "@portfolio/popups/ContractNoteManagerPopup";
import {useSharingContext} from "@dashboard/contexts/SharingContext";

const PortfolioUtilButtons = ({
                                  isAutoRefreshing,
                                  toggleAutoRefreshing,
                                  announcementStocksOnly,
                                  toggleAnnouncementStocksOnly,

                                  showImportPopup,
                                  setShowImportPopup,
                                  handleFileSelect,
                                  file,
                                  aggregatedSortedStocksCurrentClientTab,
                                  pfQuotes,
                                  activeTabRef,
                                  exportToCSV,
                                  exportToExcel,
                                  setSearchQuery,
                                  setScrollToSymbol,
                                  showZeroQuantity,
                                  setShowZeroQuantity,
                              }) => {

    const [showSearchPopup, setShowSearchPopup] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const searchButtonRef = useRef(null);
    const popupRef = useRef(null);
    const [popupPosition, setPopupPosition] = useState({top: 0, left: 0});
    const {inputRef} = useRowSelectionContext();

    const [showActionsMenu, setShowActionsMenu] = useState(false);
    const actionsButtonRef = useRef(null);
    const [showCnManager, setShowCnManager] = useState(false);
    const { showFormattedNumbers, setShowFormattedNumbers } = useSharingContext();

    useSearchEscapeHandler({
        setSearchQuery,
        setShowSearchPopup,
        setInputValue,
        setScrollToSymbol
    });

    const updatePopupPosition = () => {
        const buttonRect = searchButtonRef.current.getBoundingClientRect();
        setPopupPosition({
            top: buttonRect.top - 110 + window.scrollY,
            left: buttonRect.left + 220 + window.scrollX,
        });
    };

    useEffect(() => {
        if (showSearchPopup) {
            updatePopupPosition();
            inputRef.current?.focus();
        }
    }, [showSearchPopup]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!actionsButtonRef.current) return;
            const btn = actionsButtonRef.current;
            const menu = document.querySelector(".pf-actions-menu");

            if (
                btn &&
                !btn.contains(e.target) &&
                menu &&
                !menu.contains(e.target)
            ) {
                setShowActionsMenu(false);
            }
        };

        if (showActionsMenu) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showActionsMenu]);

    const handleClickImport = () => {
        setShowActionsMenu(false);
        setShowImportPopup(true);
    };

    const handleClickExportCsv = () => {
        setShowActionsMenu(false);
        exportToCSV(aggregatedSortedStocksCurrentClientTab, pfQuotes, activeTabRef.current);
    };

    const handleClickExportExcel = () => {
        setShowActionsMenu(false);
        exportToExcel(aggregatedSortedStocksCurrentClientTab, pfQuotes, activeTabRef.current);
    };

    const handleClickDeleteCn = () => {
        setShowActionsMenu(false);
        setShowCnManager(true);
    };

    return (
        <div className="pf-util-buttons">
            {/* Search button + popup (unchanged) */}
            <button
                className="pf-search-button"
                ref={searchButtonRef}
                onClick={() => setShowSearchPopup((prev) => !prev)}
            >
                Search
            </button>

            {/* Auto-refresh toggle */}
            <button
                className={isAutoRefreshing ? "pf-search-button" : "pf-search-button pf-search-button-danger"}
                onClick={toggleAutoRefreshing}
                style={!isAutoRefreshing ? { backgroundColor: '#ff4d4d', color: '#fff' } : undefined}
            >
                {isAutoRefreshing ? "Refreshing" : "Frozen"}
            </button>

            {/* Announcement-only mode indicator */}
            {announcementStocksOnly && (
                <div
                    className="pf-announcement-only-indicator"
                    title="Showing only stocks with announcements"
                    style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '1px 5px' }}
                >
                    <button
                        className="pf-announcement-button"
                        style={{ cursor: 'default' }}
                    >
                        A
                    </button>
                    <span style={{ fontSize: '14px', color: '#555' }}>
            Stocks
        </span>
                </div>
            )}

            {showSearchPopup && (
                <div
                    className="search-popup"
                    ref={popupRef}
                    style={{
                        position: 'absolute',
                        top: `${popupPosition.top}px`,
                        left: `${popupPosition.left}px`,
                        zIndex: 9999,
                        backgroundColor: 'white',
                        padding: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        borderRadius: '6px',
                        border: '1px solid #ccc'
                    }}
                >
                    <input
                        type="text"
                        ref={inputRef}
                        className="pf-search-input"
                        placeholder="Type to search"
                        value={inputValue}
                        onChange={(e) => {
                            const query = e.target.value.toUpperCase();
                            setInputValue(query);
                            setSearchQuery(query); // live update
                        }}
                    />
                </div>
            )}

            {/* New Actions dropdown */}
            <div className="pf-actions-dropdown">
                <button
                    ref={actionsButtonRef}
                    className="pf-actions-button"
                    onClick={() => setShowActionsMenu(prev => !prev)}
                >
                    Actions
                    <span className="pf-actions-caret">▾</span>
                </button>

                {showActionsMenu && (
                    <div className="pf-actions-menu">
                        <button
                            className="pf-actions-item"
                            onClick={handleClickImport}
                        >
                            Import Contract Note
                        </button>
                        <button
                            className="pf-actions-item"
                            onClick={() => {
                                setShowActionsMenu(false);
                                setShowFormattedNumbers(prev => !prev);
                            }}
                        >
                            {showFormattedNumbers ? "Display Actual Amounts" : "Display Formatted Amounts"}
                        </button>
                        <button
                            className="pf-actions-item"
                            onClick={handleClickExportCsv}
                        >
                            Export CSV
                        </button>
                        <button
                            className="pf-actions-item"
                            onClick={handleClickExportExcel}
                        >
                            Export Excel
                        </button>
                        <button
                            className="pf-actions-item pf-actions-item-danger"
                            onClick={handleClickDeleteCn}
                        >
                            Delete Contract Note…
                        </button>
                        <button
                            className="pf-actions-item"
                            onClick={() => {
                                setShowActionsMenu(false);
                                setShowZeroQuantity(prev => !prev);
                            }}
                        >
                            {showZeroQuantity ? "Hide zero quantities" : "Display zero quantities"}
                        </button>

                        <button
                            className="pf-actions-item"
                            onClick={() => {
                                setShowActionsMenu(false);
                                toggleAnnouncementStocksOnly();
                            }}
                        >
                            {announcementStocksOnly ? "Show all stocks" : "Announcement only stocks"}
                        </button>


                    </div>
                )}
            </div>

            {/* Old Import popup kept as-is, just triggered via dropdown now */}
            {showImportPopup && (
                <div className="popup-content small">
                    <h3>Select File to Import</h3>
                    <div className="file-upload-container">
                        <input
                            type="file"
                            id="fileInput"
                            accept=".pdf,.csv"
                            onChange={handleFileSelect}
                            className="file-input"
                        />
                        <label htmlFor="fileInput" className="file-label">Choose File</label>
                        <span className="file-name">{file ? file.name : "No file chosen"}</span>
                    </div>
                    <div className="contract-buttons">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => setShowImportPopup(false)}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {file && <ImportContractNote file={file}/>}

            {/* New Contract Note manager popup */}
            {showCnManager && (
                <ContractNoteManagerPopup
                    isOpen={showCnManager}
                    onClose={() => setShowCnManager(false)}
                />
            )}
        </div>
    );
};

export default PortfolioUtilButtons;